import React, {
  useState,
  useEffect
} from 'react';

import { Form, Field } from "react-final-form";

import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  CircularProgress,
  Grid,
  Typography,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Select,
  Checkbox,
  InputLabel,
  MenuItem,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

import {
  FileUploadField,
  UserSelectorField
} from '../../general';

import {
  BaseAPI,
} from '../../../data/BaseAPI';

import localization from '../../../utils/localizations';


const useStyles = makeStyles((theme) => ({
  mainBody: {
    padding: 10,
  },
  mainGrid: {
    marginTop: 20,
  },
  gridLabelBolder: {
    textAlign: 'right',
    fontWeight: 500,
  },
  gridLabel: {
    textAlign: 'right',
    color: '#555555',
  },
  alignRight: {
    textAlign: 'right',
  },
}));

interface Values {
  title?: string;
  primary_advisor?: string;
  additional_advisors?: string[];
  advisor_research?: boolean;
  proposal_docx?: string;
  proposal_pdf?: string;
  ksm?: string;
}

function ThesisProposalForm(props: {onSubmitted: any, onCancelled: any, values?: Values}) {
  const classes = useStyles();

  const maxUploadSize = 20*1024*1024;
  const [isSaving, setIsSaving] = useState(false);

  const onSubmit = async (values: Values) => {
    if (isSaving) return;

    const api = new BaseAPI();
    setIsSaving(true);
    try {
      const [result, response, error] = await api.post(values, 's2/proposal/');
      setIsSaving(false);
  
      console.log('result', result)
      if (result.success) {
        props.onSubmitted();
      }
      else {
        return result.errors;
      } 
    } catch (error) {
      setIsSaving(false);
      console.log((error as any).message, (error as any).status, (error as any).errorData);
      return (error as any).errorData;
    }
  };

  const onValidate = (values: Values) => {
    const errors: any = {}

    if (!values.title) errors.title = localization.required;
    if (!values.primary_advisor) errors.primary_advisor = localization.required;
    if (!values.proposal_pdf) errors.proposal_pdf = localization.required;

    return errors;
  }

  return (
    <Form
      onSubmit={onSubmit}
      validate={onValidate}
      initialValues={props.values ? props.values : {}}
      render={({ handleSubmit, form, submitting, pristine, values }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} className={classes.mainGrid}>
            <Field name="title">
              {props => (
                <Grid item xs={12} sm={12}>
                  <FormControl fullWidth error={props.meta.error && props.meta.touched ? !!props.meta.error : undefined}>
                    <TextField
                      id={props.input.name}
                      label={localization.proposalTitle}
                      variant="outlined"
                      name={props.input.name}
                      value={props.input.value}
                      onChange={props.input.onChange}
                      error={(props.meta.error || props.meta.submitError) && props.meta.touched ? true: false}
                      helperText={(props.meta.error || props.meta.submitError) && props.meta.touched ? props.meta.error || props.meta.submitError : undefined}
                    />
                  </FormControl>
                </Grid>
              )}
            </Field>

            <Grid item xs={12}>
              <Typography variant="body2">{localization.thesisDiscussedWith}:</Typography>
            </Grid>

            <Field name="primary_advisor">
              {props => (
                <Grid item xs={12}>
                  <UserSelectorField
                    label={localization.prospectivePrimaryAdvisor}
                    role="lecturers"
                    filter={{active: true, available_as_advisor: true}}
                    values={props.input.value}
                    excludes={values.additional_advisors}
                    onChanged={(value) => {
                      props.input.onChange(value);
                    }}
                    error={(props.meta.error || props.meta.submitError) && props.meta.touched ? (props.meta.error || props.meta.submitError) : undefined}
                  />
                </Grid>
              )}
            </Field>

            <Field name="additional_advisors">
              {props => (
                <Grid item xs={12}>
                  <UserSelectorField
                    label={localization.additionalProspectiveAdvisors}
                    role="lecturers"
                    filter={{active: true, available_as_advisor: true}}
                    multiple={true}
                    values={props.input.value}
                    excludes={values.primary_advisor ? [values.primary_advisor] : undefined}
                    onChanged={(values) => {
                      props.input.onChange(values);
                    }}
                    helpText={localization.fillIfExist}
                    error={(props.meta.error || props.meta.submitError) && props.meta.touched ? (props.meta.error || props.meta.submitError) : undefined}
                  />
                </Grid>
              )}
            </Field>

            <Field name="advisor_research">
                {props => (
                  <Grid item xs={12} sm={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={props.input.value}
                          onChange={props.input.onChange}
                          name={props.input.name}
                          color="primary"
                        />
                      }
                      label={localization.advisorResearch}
                    />
                  </Grid>
                )}
              </Field>

            <Grid item xs={4} sm={2}>
              <Typography variant="body1" className={classes.gridLabel}>KSM</Typography>
            </Grid>
            <Grid item xs={8} sm={10}>
              <Field name="ksm">
                {props => (
                  <FileUploadField
                    accept=".pdf,.jpg,.png,.pdf"
                    title={localization.uploadKSM}
                    maxSize={5*1024*1024}
                    values={props.input.value}
                    fileKind="KSM"
                    onChanged={(file) => {
                      props.input.onChange(file);
                    }}
                    error={(props.meta.error || props.meta.submitError) && props.meta.touched ? (props.meta.error || props.meta.submitError) : undefined}
                  />
                )}
              </Field>
            </Grid>

            <Grid item xs={4} sm={2}>
              <Typography variant="body1" className={classes.gridLabel}>Proposal (.docx)</Typography>
            </Grid>
            <Grid item xs={8} sm={10}>
              <Field name="proposal_docx">
                {props => (
                  <FileUploadField
                    accept=".docx"
                    title={localization.uploadProposal}
                    maxSize={maxUploadSize}
                    values={props.input.value}
                    fileKind="Proposal"
                    onChanged={(file) => {
                      props.input.onChange(file);
                    }}
                    error={(props.meta.error || props.meta.submitError) && props.meta.touched ? (props.meta.error || props.meta.submitError) : undefined}
                  />
                )}
              </Field>
            </Grid>

            <Grid item xs={4} sm={2}>
              <Typography variant="body1" className={classes.gridLabel}>Proposal (.pdf)</Typography>
            </Grid>
            <Grid item xs={8} sm={10}>
              <Field name="proposal_pdf">
                {props => (
                  <FileUploadField
                    accept=".pdf"
                    title={localization.uploadProposal}
                    maxSize={maxUploadSize}
                    values={props.input.value}
                    fileKind="Proposal"
                    onChanged={(file) => {
                      props.input.onChange(file);
                    }}
                    error={(props.meta.error || props.meta.submitError) && props.meta.touched ? (props.meta.error || props.meta.submitError) : undefined}
                  />
                )}
              </Field>
            </Grid>

            <Grid item xs={6}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                type="submit"
                disabled={isSaving}
              >Submit {localization.proposal}</Button>
              {isSaving && <CircularProgress size={20} style={{marginLeft: 10}} />}
            </Grid>
            <Grid item xs={6} className={classes.alignRight}>
              <Button
                variant="contained"
                onClick={props.onCancelled}
              >Cancel</Button>
            </Grid>
          </Grid>
        </form>
      )}
    />
  );
}

export {ThesisProposalForm};